import React, { useRef, useEffect, useMemo } from 'react';
import { RouteComponentProps, useLocation, useRouteMatch } from 'react-router';
import PageContainer from '../PageContainer';
import PaddedContent from '../../components/housing/PaddedContent';
import SmallLiftup from '../../components/liftup/SmallLiftup';
import Grid from '../../components/common/Grid';
import { Body2, LinkButton, AnchorButton } from '../../components/common';
import { H1 } from '../../components/basic/Text';
import { useMessageGetter } from 'react-message-context';
import styled from 'styled-components/macro';
import Pagination from '../../components/common/Pagination';
import { useKennoTenant, MFilesLink, MfilesFormType } from '../../context/kennoTenantContext';
import { loadPublicNotices, loadDocuments } from '../../context/kennoTenantActions';
import {
  getBackgroundForNoticeType,
  getNoticeDisplayTitle,
  getPictogramForNoticeType,
  NOTICE_PAGE_QUERY_PARAM,
} from './notice-utils';
import { formatDateTime, formatDate } from '../../utils';
import { PublicNotice } from '../../types/notices';
import { LOAD_STATES } from '../../context/common';
import Loader from '../../components/basic/Loader';

const ITEMS_PER_PAGE = 12;
const NOTICE_FILTER_YEARS = 2;

const Title = styled(H1)`
  margin-bottom: 40px;
`;

const NoticeListPage = ({ history }: RouteComponentProps) => {
  const t = useMessageGetter('myBuilding.notices');
  const tDocuments = useMessageGetter('myBuilding.documents');
  const ref = useRef<HTMLElement | null>(null);
  const { url: currentUrl } = useRouteMatch();
  const query = new URLSearchParams(useLocation().search);
  const [{ publicNotices, documents }, dispatch] = useKennoTenant();

  useEffect(() => {
    loadPublicNotices(dispatch);
    loadDocuments(dispatch);
  }, [dispatch]);

  const pageParam = parseInt(query.get(NOTICE_PAGE_QUERY_PARAM) || '', 10) || 1;

  const documentsAndNotices = useMemo(() => {
    const noticesToShow = publicNotices.data || [];

    const documentLinks = documents.data
      ? [documents.data?.costCenterLinks, documents.data?.apartmentLinks, documents.data?.buildingLinks].flat()
      : [];
    // filtering and add publishedAt and type for sorting etc.
    const documentsFilteredByFolder = documentLinks.filter((d) => d.Folder1.toLowerCase() === 'tiedotteet');

    const documentsToShow = documentsFilteredByFolder.map((d) => {
      // parse createdAt as publishedAt
      let publishedAt;
      try {
        const year = parseInt(d.creationDate.split('.')[2]);
        const month = parseInt(d.creationDate.split('.')[1]);
        const day = parseInt(d.creationDate.split('.')[0]);
        publishedAt = new Date(year, month, day);
      } catch (e) {
        //fall back to current date
        publishedAt = new Date();
      }
      return {
        ...d,
        publishedAt: publishedAt,
        formType: MfilesFormType.DOCUMENT,
      };
    });

    const currentTime = new Date().getTime();

    return [...noticesToShow, ...documentsToShow]
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
      .filter((d) => currentTime - new Date(d.publishedAt).getTime() < NOTICE_FILTER_YEARS * 365 * 24 * 60 * 60 * 1000);
  }, [publicNotices, documents]);

  const numberOfPages = Math.ceil(documentsAndNotices.length / ITEMS_PER_PAGE);

  // Ensure query param is between 1 and total number of pages, default to 1 if value is invalid.
  const currentPage = pageParam < 1 || pageParam > numberOfPages ? 1 : pageParam;

  const currentPageNotices: (PublicNotice | MFilesLink)[] = useMemo(() => {
    return documentsAndNotices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  }, [currentPage, documentsAndNotices]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > numberOfPages || newPage === currentPage) {
      return;
    }
    // Keep page in query params
    query.set(NOTICE_PAGE_QUERY_PARAM, newPage.toString());
    history.push({ search: query.toString() });

    // Using setTimeout fixes the scroll on certain mobile views
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  };

  return (
    <PageContainer history={history}>
      <PaddedContent>
        <section ref={ref}>
          <Title>{t('title')}</Title>
          <Grid>
            {currentPageNotices.length === 0 && <Body2>{t('noData')}</Body2>}
            {currentPageNotices.map((notice, idx) => {
              const button =
                notice?.formType === MfilesFormType.DOCUMENT ? (
                  <AnchorButton primary href={notice.href}>
                    {tDocuments('downloadPdf')}
                  </AnchorButton>
                ) : (
                  <LinkButton to={`${currentUrl}/${notice.id}?${NOTICE_PAGE_QUERY_PARAM}=${currentPage}`} primary>
                    {t('readMore')}
                  </LinkButton>
                );
              const heading =
                notice?.formType === MfilesFormType.DOCUMENT
                  ? `${tDocuments('heading')} - ${notice.Folder1}`
                  : t('committee');
              const title =
                notice?.formType === MfilesFormType.DOCUMENT ? notice.name : getNoticeDisplayTitle(notice, t);
              const subTitle =
                notice?.formType === MfilesFormType.DOCUMENT
                  ? formatDate(notice?.publishedAt?.toISOString())
                  : formatDateTime(notice.publishedAt);
              const background = notice?.formType === MfilesFormType.DOCUMENT ? notice.Folder2 : notice.formType;
              return (
                <SmallLiftup
                  key={notice.id || idx}
                  title={title}
                  subTitle={subTitle}
                  heading={heading}
                  pictogram={getPictogramForNoticeType(notice.formType)}
                  mainButton={button}
                  background={getBackgroundForNoticeType(background)}
                />
              );
            })}
            {(publicNotices.loadState === LOAD_STATES.BUSY || documents.loadState === LOAD_STATES.BUSY) && <Loader />}
          </Grid>
          {numberOfPages > 1 && (
            <Pagination handlePageChange={handlePageChange} numberOfPages={numberOfPages} currentPage={currentPage} />
          )}
        </section>
      </PaddedContent>
    </PageContainer>
  );
};

export default NoticeListPage;
